import { ReactNode } from "react";
import { flexRender, ColumnDef, ColumnFiltersState, OnChangeFn, VisibilityState } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";
import { useDataTable } from "@core/features/dataTable";
import {
  Table as TableUI,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TablePagination,
} from "@themes/pollinator/ui/Table";

export type Props<T> = {
  /** Data array to display in the table */
  data: T[];
  /** Column definitions for the table */
  columns: ColumnDef<T>[];
  /** Additional CSS class for the container */
  className?: string;
  /** Initial page size (default: 10) */
  initialPageSize?: number;
  /** Initial column visibility state */
  columnVisibility?: VisibilityState;
  /** Controlled column filters state */
  columnFilters?: ColumnFiltersState;
  /** Callback when column filters change */
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /** Custom filter component to render */
  filterComponent?: ReactNode;
  /** Whether to show the search input */
  isWithSearch?: boolean;
};

const AnimatedTableRow = motion(TableRow);

/**
 * Styled data table component with TanStack Table integration.
 * Uses the useDataTable hook from core and styled Table components.
 *
 * @example
 * import { DataTable } from "@themes/pollinator/ui/DataTable";
 *
 * const columns = [
 *   { accessorKey: "name", header: "Name" },
 *   { accessorKey: "email", header: "Email" },
 * ];
 *
 * <DataTable
 *   data={users}
 *   columns={columns}
 *   isWithSearch
 *   initialPageSize={20}
 * />
 */
export function DataTable<T>({
  data,
  columns,
  initialPageSize = 10,
  columnVisibility: initialColumnVisibility = {},
  columnFilters = [],
  onColumnFiltersChange = () => {},
  filterComponent,
  isWithSearch = false,
  className,
}: Props<T>) {
  const { table, globalFilter, setGlobalFilter } = useDataTable({
    data,
    columns,
    initialPageSize,
    initialColumnVisibility,
    columnFilters,
    onColumnFiltersChange,
  });

  return (
    <div className={twMerge("flex flex-col gap-8 w-full", className)}>
      {(isWithSearch || filterComponent) && (
        <div className="mb-4 flex w-full flex-col items-center md:mb-0 md:flex-row md:items-center md:justify-between">
          {filterComponent}
          {isWithSearch && (
            <div className="flex justify-end">
              <div className="relative flex flex-col gap-1 transition-opacity duration-100">
                <span className="absolute left-3 top-3 flex items-center w-4 h-4 text-brand-600">
                  <Search className="w-4 h-4" />
                </span>
                <input
                  name="search"
                  placeholder="Search records"
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className={twMerge(
                    "relative border rounded-sm bg-transparent text-sm shadow-sm transition-colors px-3 py-2 w-full",
                    "disabled:cursor-not-allowed disabled:bg-brand-100 disabled:text-brand-300 disabled:border-brand-300",
                    "placeholder:text-brand-400",
                    "pl-10"
                  )}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <TableUI>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          <AnimatePresence>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-6"
                >
                  No results found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <AnimatedTableRow
                  key={row.id}
                  layout
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.1 }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </AnimatedTableRow>
              ))
            )}
          </AnimatePresence>
        </TableBody>

        <TablePagination table={table} colSpan={columns.length} />
      </TableUI>
    </div>
  );
}

export default DataTable;
